import { createContext, useContext, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import { SnackbarAlert } from "../components/common/SnackbarAlert";

interface SnackbarState {
  open: boolean;
  message: ReactNode;
  severity: "success" | "error" | "warning" | "info";
  duration?: number;
  position?: "top-center" | "top-right" | "top-left" | "bottom-center" | "bottom-right" | "bottom-left";
}

interface SnackbarContextType {
  showSnackbar: (
    message: ReactNode,
    severity?: "success" | "error" | "warning" | "info",
    duration?: number,
    position?: SnackbarState["position"]
  ) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useGlobalSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useGlobalSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
    duration: 5000,
    position: "top-center",
  });

  const showSnackbar = (
    message: ReactNode,
    severity: "success" | "error" | "warning" | "info" = "info",
    duration: number = 5000,
    position: SnackbarState["position"] = "top-center"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
      duration,
      position,
    });
  };

  const hideSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      {createPortal(
        <SnackbarAlert
          openState={snackbar.open}
          setOpenState={hideSnackbar}
          severity={snackbar.severity}
          message={snackbar.message}
          duration={snackbar.duration}
          position={snackbar.position}
        />,
        document.body
      )}
    </SnackbarContext.Provider>
  );
};
